import React, { useState, useEffect } from 'react';
import { AppBar, Toolbar, IconButton, Typography, List, ListItem, ListItemText, ListItemIcon, Switch, CircularProgress } from '@mui/material';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { IoMdArrowBack } from 'react-icons/io';
import { AuthContext } from '../Context/AuthContext';
import { toast } from 'react-toastify';

const NotificationPage = () => {
  const [isToggleEmailChecked, setIsToggleEmailChecked] = useState(false);
  const [isToggleSmsChecked, setIsToggleSmsChecked] = useState(false);
  const [isToggleWhatsAppChecked, setIsToggleWhatsAppChecked] = useState(false);
  const [loading, setLoading] = useState(false);
  const { token } = React.useContext(AuthContext);

  useEffect(() => {
    const fetchNotificationSettings = async () => {
      setLoading(true);
      try {
        if (!token) {
          throw new Error('No token available');
        }

        const res = await axios.get(
          `${import.meta.env.VITE_APP_BASE_API}${import.meta.env.VITE_APP_API_VERSION}/preferences/get`, 
          {
            headers: { 
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );

        if (res.data.success) {
          const { emailNotification, smsNotification, whatsappNotification } = res.data;
          setIsToggleEmailChecked(emailNotification);
          setIsToggleSmsChecked(smsNotification);
          setIsToggleWhatsAppChecked(whatsappNotification);
        }
      } catch (error) {
        if (error.response?.status === 401) {
          window.location.href = '/login';
        } else {
          toast.error(error.response?.data?.message || 'Error fetching notification settings');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchNotificationSettings();
  }, [token]);

  const updateNotificationDetails = async (smsNotification, emailNotification, whatsappNotification) => {
    if (!token) {
      toast.error('Please login to update notifications');
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(
        `${import.meta.env.VITE_APP_BASE_API}${import.meta.env.VITE_APP_API_VERSION}/preferences/update`,
        { smsNotification, emailNotification, whatsappNotification },
        {
          withCredentials: true,
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
        }
      );
      if (res.data) {
        toast.success('Notification settings updated successfully');
      }
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error('Session expired. Please login again');
      } else {
        toast.error('Error updating notification settings');
      }
      console.error('Error updating notification details:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateEmail = async (event) => {
    const checked = event.target.checked;
    try {
      await updateNotificationDetails(isToggleSmsChecked, checked, isToggleWhatsAppChecked);
      setIsToggleEmailChecked(checked);
    } catch (error) {
      // Revert toggle if update fails
      setIsToggleEmailChecked(!checked);
    }
  }

  const updateSms = async (event) => {
    const checked = event.target.checked;
    try {
      await updateNotificationDetails(checked, isToggleEmailChecked, isToggleWhatsAppChecked);
      setIsToggleSmsChecked(checked);
    } catch (error) {
      setIsToggleSmsChecked(!checked);
    }
  }

  const updateWhatsApp = async (event) => {
    const checked = event.target.checked;
    try {
      await updateNotificationDetails(isToggleSmsChecked, isToggleEmailChecked, checked);
      setIsToggleWhatsAppChecked(checked);
    } catch (error) {
      setIsToggleWhatsAppChecked(!checked);
    }
  }

  return (
    <div>
      <AppBar position="static">
        <Toolbar>
        <Link to={"/profile"}>
            <IconButton
              size="large"
              edge="start"
              color="inherit"
              aria-label="back"
              sx={{ mr: 2 }}
            >
              <IoMdArrowBack />
            </IconButton>
          </Link>
          <Typography variant="h6">Notifications</Typography>
        </Toolbar>
      </AppBar>
      <div className="p-4">
        {loading && <CircularProgress />}
        <List>
          <ListItem>
            <ListItemIcon>
              <MailOutlineIcon />
            </ListItemIcon>
            <ListItemText primary="Email" />
            <Switch
              checked={isToggleEmailChecked}
              onChange={updateEmail}
              color="primary"
            />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <ChatBubbleOutlineIcon />
            </ListItemIcon>
            <ListItemText primary="SMS" />
            <Switch
              checked={isToggleSmsChecked}
              onChange={updateSms }
              color="primary"
            />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <ChatBubbleOutlineIcon />
            </ListItemIcon>
            <ListItemText primary="WhatsApp" />
            <Switch
              checked={isToggleWhatsAppChecked}
              onChange={updateWhatsApp }
              color="primary"
            />
          </ListItem>
        </List>
      </div>
    </div>
  );
};

export default NotificationPage;
